/**
 * Phase 16 (F10/F11/CO4) — SpecialPickerWithGroup.
 *
 * A compact two-dropdown picker used on buffet meal blocks AND
 * regular-restaurant meal rows for Chef's Special + Bakery Special
 * selection with an optional Group filter.
 *
 * Group filter narrows the item picker. "All / Ungrouped" sentinel
 * bypasses the filter. Selecting an item snapshots `id + name` into
 * the parent, and stores `group_id` separately so the filter dropdown
 * sticks across re-renders.
 */
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const ACCENT = {
  amber: {
    wrap: "border-amber-300 bg-amber-50/40",
    label: "text-amber-900",
  },
  rose: {
    wrap: "border-rose-300 bg-rose-50/40",
    label: "text-rose-900",
  },
};

const NONE = "__none__";
const ALL_GROUPS = "__all__";

export default function SpecialPickerWithGroup({
  label,
  valueId,
  valueName,
  groupId,
  itemsLib,
  groupsLib,
  onChange,
  tidPrefix,
  accent = "amber",
}) {
  const wrap = ACCENT[accent] || ACCENT.amber;

  // Filter items by group when a group is selected (and isn't the
  // "all" sentinel). Items with no group are included only when the
  // user explicitly picks the "ungrouped" sentinel; ALL_GROUPS shows
  // everything regardless.
  const effectiveGroup = groupId || ALL_GROUPS;
  const filteredItems = (itemsLib || []).filter((it) => {
    if (effectiveGroup === ALL_GROUPS) return true;
    return (it.group_id || null) === effectiveGroup;
  });

  const pickItem = (id) => {
    if (id === NONE) {
      onChange({ id: null, name: null, group_id: groupId });
      return;
    }
    const it = (itemsLib || []).find((x) => x.id === id);
    if (!it) return;
    onChange({ id: it.id, name: it.name, group_id: groupId });
  };

  const pickGroup = (gid) => {
    const next = gid === ALL_GROUPS ? null : gid;
    // Clear the chosen item when the group changes IF the current item
    // no longer lives in the new group. Avoids stale picks.
    if (next && valueId) {
      const it = (itemsLib || []).find((x) => x.id === valueId);
      if (it && (it.group_id || null) !== next) {
        onChange({ id: null, name: null, group_id: next });
        return;
      }
    }
    onChange({ id: valueId, name: valueName, group_id: next });
  };

  return (
    <div
      className={cn(
        "mb-2 flex flex-wrap items-center gap-1.5 rounded border px-1.5 py-1",
        wrap.wrap,
      )}
      data-testid={tidPrefix}
    >
      <span
        className={cn(
          "shrink-0 text-[9px] font-semibold uppercase tracking-wider",
          wrap.label,
        )}
      >
        {label}
      </span>
      {/* Group filter */}
      <Select value={effectiveGroup} onValueChange={pickGroup}>
        <SelectTrigger
          className="h-6 w-[140px] text-[10px]"
          data-testid={`${tidPrefix}-group`}
        >
          <SelectValue placeholder="Group…" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL_GROUPS}>— All groups —</SelectItem>
          {(groupsLib || []).map((g) => (
            <SelectItem key={g.id} value={g.id}>
              {g.color && (
                <span
                  className="mr-1 inline-block h-2 w-2 rounded-full align-middle"
                  style={{ backgroundColor: g.color }}
                />
              )}
              {g.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* Item picker (filtered) */}
      <Select value={valueId || NONE} onValueChange={pickItem}>
        <SelectTrigger
          className="h-6 flex-1 min-w-[160px] text-[10px]"
          data-testid={`${tidPrefix}-item`}
        >
          <SelectValue placeholder={`Pick ${label.toLowerCase()}…`} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE}>— None —</SelectItem>
          {filteredItems.length === 0 ? (
            <div className="px-2 py-1 text-[9px] italic text-muted-foreground">
              No matching {label.toLowerCase()}.
            </div>
          ) : (
            filteredItems.map((it) => (
              <SelectItem key={it.id} value={it.id}>
                {it.name}
                {it.description && (
                  <span className="ml-1 text-[9px] text-muted-foreground">
                    — {it.description}
                  </span>
                )}
              </SelectItem>
            ))
          )}
        </SelectContent>
      </Select>
    </div>
  );
}
